/**
 * Calendar Download Component
 * Download a yearly panchanga calendar PDF with location, language,
 * and paper size selection, download tracking, and post-download feedback.
 *
 * Uses the shared LocationSearch component for consistent city search UX
 * across all pages.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Download, Loader2, Star, CheckCircle2, ChevronDown } from 'lucide-react';
import { Card } from '../ui/card';
import { LocationSearch } from '../LocationSearch/LocationSearch';
import { LocationData } from '../../data/locations';
import { API_CONFIG } from '../../config/api';

const LOCALES = [
  { code: 'en', label: 'English' },
  { code: 'sa', label: 'संस्कृतम् (Sanskrit)' },
  { code: 'hi', label: 'हिन्दी (Hindi)' },
  { code: 'te', label: 'తెలుగు (Telugu)' },
  { code: 'kn', label: 'ಕನ್ನಡ (Kannada)' },
];

const PAPER_SIZES = [
  { code: 'a4', label: 'A4' },
  { code: 'letter', label: 'Letter' },
];

/** Hindu year: March of current year to March of next year */
const now = new Date();
const START_YEAR = now.getMonth() >= 2 ? now.getFullYear() : now.getFullYear() - 1;

/** No default location - user must select their city */
const DEFAULT_LOCATION = null;

/** Estimated generation time in seconds (first-time, uncached) */
const ESTIMATED_SECONDS = 90;

interface CalendarDownloadProps {
  selectedLocale?: string;
}

const CalendarDownload: React.FC<CalendarDownloadProps> = ({ selectedLocale = 'en' }) => {
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(DEFAULT_LOCATION);
  const [locale, setLocale] = useState(selectedLocale);
  const [paperSize, setPaperSize] = useState(() => {
    // Auto-detect: US/Canada/Mexico use Letter, everyone else uses A4
    const lang = navigator.language || '';
    return lang.endsWith('-US') || lang.endsWith('-CA') || lang.endsWith('-MX') || lang === 'en-US'
      ? 'letter' : 'a4';
  });
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);

  // Feedback state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [previewImage, setPreviewImage] = useState<{ src: string; label: string } | null>(null);
  const [downloaded, setDownloaded] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const locationId = selectedLocation?.id || '';

  // Countdown timer while generating
  useEffect(() => {
    if (generating) {
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [generating]);

  // Reset download when language or paper size changes
  useEffect(() => {
    setDownloadUrl(null);
    setDownloaded(false);
    setError(null);
  }, [locale, paperSize]);

  const handleLocationSelect = (location: LocationData | null) => {
    setSelectedLocation(location);
    setError(null);
    setDownloadUrl(null);
    setDownloaded(false);
  };

  const handleGenerate = async () => {
    if (!selectedLocation) {
      setError('Please select a location first.');
      return;
    }

    setGenerating(true);
    setError(null);
    setDownloadUrl(null);
    setDownloaded(false);
    setFeedbackSubmitted(false);
    setRating(0);
    setFeedbackText('');

    try {
      const base = API_CONFIG.baseUrl || window.location.origin;
      const params = new URLSearchParams({
        location: [selectedLocation.name, selectedLocation.state, selectedLocation.country].filter(Boolean).join(', '),
        latitude: String(selectedLocation.latitude),
        longitude: String(selectedLocation.longitude),
        locale,
        paper_size: paperSize,
      });
      const url = `${base}${API_CONFIG.endpoints.calendarDownload}?${params}`;

      const response = await fetch(url);

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        setError(data?.detail || `Generation failed (${response.status}). Please try again.`);
        return;
      }

      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.includes('application/json')) {
        // Production: Lambda returns JSON with presigned S3 URL
        const data = await response.json();
        setDownloadUrl(data.download_url);
      } else {
        // Local dev: FastAPI returns PDF bytes directly
        const blob = await response.blob();
        setDownloadUrl(URL.createObjectURL(blob));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadClick = () => {
    if (!downloadUrl) return;
    // Trigger download via hidden anchor
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `panchanga-${locationId || 'calendar'}-${locale}.pdf`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setDownloaded(true);
  };

  const handleFeedbackSubmit = async () => {
    if (rating === 0) return;
    setSubmittingFeedback(true);
    try {
      const base = API_CONFIG.baseUrl || window.location.origin;
      await fetch(`${base}/api/v1/calendar/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: locationId,
          locale,
          year: START_YEAR,
          rating,
          feedback_text: feedbackText,
        }),
      });
    } catch {
      // Feedback submission is best-effort
    }
    setFeedbackSubmitted(true);
    setSubmittingFeedback(false);
  };

  const remaining = Math.max(0, ESTIMATED_SECONDS - elapsed);
  const progressPct = Math.min(95, (elapsed / ESTIMATED_SECONDS) * 100);

  const selectClasses = "w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring appearance-none cursor-pointer";

  const LOCALE_PREVIEWS = [
    { code: 'en', label: 'English' },
    { code: 'sa', label: 'संस्कृतम्' },
    { code: 'hi', label: 'हिन्दी' },
    { code: 'te', label: 'తెలుగు' },
    { code: 'kn', label: 'ಕನ್ನಡ' },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-brand dark:text-brand-dark">
          Download Yearly Calendar
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Fortnightly panchanga with <span className="font-medium text-foreground/70">tithi</span>, <span className="font-medium text-foreground/70">nakshatra</span>, <span className="font-medium text-foreground/70">yoga</span>, <span className="font-medium text-foreground/70">karana</span> transitions,{' '}
          <span className="font-medium text-foreground/70">kala vishesha</span>, and <span className="font-medium text-foreground/70">planetary transits</span>
          <br />
          <span className="text-brand dark:text-brand-dark font-medium">March {START_YEAR} through March {START_YEAR + 1}</span>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-start">
      <Card className="p-6 space-y-5">
        {/* Location selector */}
        <div className="space-y-1.5">
          <LocationSearch
            onLocationSelect={handleLocationSelect}
            initialLocation={DEFAULT_LOCATION}
            placeholder="Search for your city..."
          />
          <p className="text-xs text-muted-foreground">
            Select any location - calendars are generated on-demand for your city. Location lookup may take a moment.
          </p>
        </div>

        {/* Language and Paper Size - side by side */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="block text-sm font-medium">Language</label>
            <div className="relative">
              <select
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                className={selectClasses}
              >
                {LOCALES.map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-medium">Paper Size</label>
            <div className="relative">
              <select
                value={paperSize}
                onChange={(e) => setPaperSize(e.target.value)}
                className={selectClasses}
              >
                {PAPER_SIZES.map((p) => (
                  <option key={p.code} value={p.code}>{p.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Generate / Progress / Download Ready */}
        {!generating && !downloadUrl && (
          <button
            onClick={handleGenerate}
            disabled={!selectedLocation}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-5 w-5" />
            Generate Calendar
          </button>
        )}

        {generating && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-brand dark:text-brand-dark font-medium">
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating your calendar...
              </span>
              <span className="text-muted-foreground tabular-nums">
                {remaining > 0 ? `~${remaining}s remaining` : 'Almost there...'}
              </span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-amber-600 dark:bg-amber-500 transition-all duration-1000 ease-linear"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Computing panchanga for 365 days. First-time generation takes longer - subsequent downloads are instant.
            </p>
          </div>
        )}

        {downloadUrl && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-medium text-sm">
              <CheckCircle2 className="h-5 w-5" />
              Calendar ready!
            </div>
            <button
              onClick={handleDownloadClick}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-green-700 hover:bg-green-800 dark:bg-green-600 dark:hover:bg-green-700 text-white font-medium transition-colors"
            >
              <Download className="h-5 w-5" />
              Download PDF
            </button>
            <button
              onClick={() => { setDownloadUrl(null); setDownloaded(false); }}
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Generate for a different location or language
            </button>
          </div>
        )}

        {/* Post-download feedback */}
        {downloaded && !feedbackSubmitted && (
          <div className="pt-4 border-t border-border space-y-3">
            <p className="text-sm font-medium">How was the calendar?</p>

            {/* Star rating */}
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-0.5 transition-colors"
                  aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                >
                  <Star
                    className={`h-6 w-6 ${
                      star <= (hoverRating || rating)
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-muted-foreground/40'
                    }`}
                  />
                </button>
              ))}
            </div>

            {/* Feedback text */}
            <textarea
              placeholder="Any suggestions or feedback? (optional)"
              className="w-full h-20 rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
            />

            <button
              onClick={handleFeedbackSubmit}
              disabled={rating === 0 || submittingFeedback}
              className="px-4 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        )}

        {/* Thank you message */}
        {feedbackSubmitted && (
          <div className="pt-4 border-t border-border">
            <p className="text-sm text-brand dark:text-brand-dark font-medium">
              Thank you for your feedback!
            </p>
          </div>
        )}
      </Card>

      {/* Preview thumbnails */}
      <div className="hidden lg:flex flex-col items-center space-y-3 -mt-8 mr-[-7rem]">
        <p className="text-xs font-medium text-muted-foreground text-center uppercase tracking-wide">Preview</p>
        <div className="grid grid-cols-2 gap-2 w-[260px]">
          {LOCALE_PREVIEWS.slice(0, 4).map((lp) => (
            <button
              key={lp.code}
              onClick={() => setPreviewImage({ src: `/previews/calendar-full-${lp.code}.jpg`, label: lp.label })}
              className={`group relative rounded-lg overflow-hidden border-2 transition-all cursor-zoom-in ${
                locale === lp.code
                  ? 'border-amber-500 shadow-md shadow-amber-500/20'
                  : 'border-transparent hover:border-border'
              }`}
            >
              <img
                src={`/previews/calendar-preview-${lp.code}.jpg`}
                srcSet={`/previews/calendar-preview-${lp.code}.jpg 1x, /previews/calendar-preview-${lp.code}@2x.jpg 2x`}
                width={122}
                height={86}
                alt={`Calendar preview in ${lp.label}`}
                className="w-full h-auto"
                loading="lazy"
              />
              <span className={`absolute bottom-0 inset-x-0 text-[10px] font-medium py-0.5 text-center ${
                locale === lp.code
                  ? 'bg-amber-600 text-white'
                  : 'bg-black/50 text-white/80'
              }`}>
                {lp.label}
              </span>
            </button>
          ))}
        </div>
        {/* 5th thumbnail centered */}
        <div className="w-[126px]">
          {LOCALE_PREVIEWS.slice(4).map((lp) => (
            <button
              key={lp.code}
              onClick={() => setPreviewImage({ src: `/previews/calendar-full-${lp.code}.jpg`, label: lp.label })}
              className={`group relative rounded-lg overflow-hidden border-2 transition-all cursor-zoom-in w-full ${
                locale === lp.code
                  ? 'border-amber-500 shadow-md shadow-amber-500/20'
                  : 'border-transparent hover:border-border'
              }`}
            >
              <img
                src={`/previews/calendar-preview-${lp.code}.jpg`}
                srcSet={`/previews/calendar-preview-${lp.code}.jpg 1x, /previews/calendar-preview-${lp.code}@2x.jpg 2x`}
                width={122}
                height={86}
                alt={`Calendar preview in ${lp.label}`}
                className="w-full h-auto"
                loading="lazy"
              />
              <span className={`absolute bottom-0 inset-x-0 text-[10px] font-medium py-0.5 text-center ${
                locale === lp.code
                  ? 'bg-amber-600 text-white'
                  : 'bg-black/50 text-white/80'
              }`}>
                {lp.label}
              </span>
            </button>
          ))}
        </div>
      </div>
      </div>

      {/* Lightbox modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm cursor-zoom-out"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-[95vw] max-h-[95vh] p-2">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 z-10 h-8 w-8 flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
              aria-label="Close preview"
            >
              &times;
            </button>
            <img
              src={previewImage.src}
              alt={`Calendar preview in ${previewImage.label}`}
              className="max-w-full max-h-[92vh] rounded-lg shadow-2xl"
            />
            <p className="text-center text-white/80 text-sm mt-2">{previewImage.label}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarDownload;
